import React, { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { toast } from "sonner";
import { endOfMonth, format, startOfMonth } from "date-fns";
import AdminLayout from "../../../layouts/AdminLayout";
import RequireAuth from "../../../auth/middleware/RequireAuth";
import MenuTabs from "../components/MenuTabs";
import DashboardStatsGrid from "../components/DashboardStatsGrid";
// import SourceOfBookingCharts from "../components/SourceOfBookingCharts";
// import OccupancyChart from "../components/OccupancyChart";
import PropertyContent from "../components/PropertyContent";
import GroupContent from "../components/GroupContent";
// import GrowthContent from "../components/GrowthContent";
import RoomReportContent from "../components/RoomReportContent";
import ReservationsTable from "../components/ReservationsTable"; // <-- 1. Import kembali ReservationsTable
import useCmSourceOfBooking from "../../../services/statistic-data/hooks/useCmSourceOfBooking"; // <-- 2. Import hook baru
import { formatPrice } from "../../../shared/utils/formatPrice";
import GrowthContentV4 from "../components/GrowthContentV4";
import SourceOfBookingCharts_Highcharts from "../components/SourceOfBookingCharts_Highcharts";
import OccupancyHighChart from "../components/OccupancyHighChart";

// Definisikan kembali tipe data untuk tabel
interface ReservationData {
    agent: React.ReactNode;
    percentage: React.ReactNode;
    reservations: React.ReactNode;
    nights: React.ReactNode;
    avgStay: React.ReactNode;
    avgEarning: React.ReactNode;
    totalEarning: React.ReactNode;
}

interface TabChangeHandler {
    (tab: string): void;
}

const ChannelManagerReport: React.FC = () => {
    const [activeTab, setActiveTab] = useState("dashboard");

    const handleTabChange: TabChangeHandler = (tab) => {
        setActiveTab(tab);
    };

    // Definisikan kembali kolom untuk tabel
    const columnsReservations: { label: string; key: string }[] = [
        { label: "Agent Name", key: "agent" },
        { label: "Percentage", key: "percentage" },
        { label: "Number of Reservations", key: "reservations" },
        { label: "Number of Nights", key: "nights" },
        { label: "Average Length of Stay", key: "avgStay" },
        { label: "Average Revenue (IDR)", key: "avgEarning" },
        { label: "Total Revenue (IDR)", key: "totalEarning" },
    ];

    const now = new Date();
    const startDate = format(startOfMonth(now), 'yyyy-MM-dd');
    const endDate = format(endOfMonth(now), 'yyyy-MM-dd');

    // 3. Panggil hook baru untuk mendapatkan data tabel
    const { data: sourceData, isFetching: isFetchingTableData } = useCmSourceOfBooking({
        filters: {
            start_date: startDate,
            end_date: endDate,
        }
    });

    const [reservationsData, setReservationsData] = useState<ReservationData[]>([]);

    // Toast notifikasi saat data dashboard berhasil di-load
    useEffect(() => {
        if (!isFetchingTableData && sourceData) {
            toast.success('Dashboard data loaded successfully.');
        }
    }, [sourceData, isFetchingTableData]);

    // 4. Buat useEffect untuk memformat data dari hook ke dalam state
    useEffect(() => {
        if (sourceData) {
            // Format data utama
            const formattedData = sourceData.data.map((item: any) => ({
                agent: item.name,
                percentage: `${parseFloat(item.percentage).toFixed(2)}%`,
                reservations: item.nb_reservations,
                nights: item.nb_nights,
                avgStay: parseFloat(item.avg_length_of_stay).toFixed(2),
                avgEarning: formatPrice(parseFloat(item.avg_earning)),
                totalEarning: formatPrice(parseFloat(item.total_earning)),
            }));

            // Format data baris total
            const totalRow = {
                agent: <strong>Total</strong>,
                percentage: <strong>{`${sourceData.total.percentage.toFixed(2)}%`}</strong>,
                reservations: <strong>{sourceData.total.nb_reservations}</strong>,
                nights: <strong>{sourceData.total.nb_nights}</strong>,
                avgStay: <strong>{parseFloat(sourceData.total.avg_length_of_stay).toFixed(2)}</strong>,
                avgEarning: <strong>{formatPrice(parseFloat(sourceData.total.avg_earning))}</strong>,
                totalEarning: <strong>{formatPrice(parseFloat(sourceData.total.total_earning))}</strong>,
            };

            // Gabungkan data utama dengan baris total
            setReservationsData([...formattedData, totalRow]);
        }
    }, [sourceData]);

    return (
        <AdminLayout>
            <div className="container-fluid">
                <Container className="my-4" fluid>
                    <MenuTabs activeTab={activeTab} onTabChange={handleTabChange} />
                </Container>

                {activeTab === "dashboard" && (
                    <RequireAuth requiredRoles={['dev']} requiredPermissions={['pms_report_view']} asComponent>
                        <Container fluid className="dashboard-content">

                            <DashboardStatsGrid startDate={startDate} endDate={endDate} />

                            {/* <SourceOfBookingCharts startDate={startDate} endDate={endDate} /> */}
                            <SourceOfBookingCharts_Highcharts startDate={startDate} endDate={endDate} />

                            {/* 5. Tampilkan kembali ReservationsTable */}
                            <ReservationsTable
                                data={reservationsData}
                                columns={columnsReservations}
                                isFetching={isFetchingTableData}
                            />

                            {/* <OccupancyChart /> */}
                            <OccupancyHighChart />
                        </Container>
                    </RequireAuth>
                )}
                {activeTab === "property" && <RequireAuth requiredRoles={['dev']} requiredPermissions={['cm_report_property_report']} asComponent><PropertyContent /></RequireAuth>}
                {activeTab === "group" && <RequireAuth requiredRoles={['dev']} requiredPermissions={['cm_report_group_report']} asComponent><GroupContent /></RequireAuth>}
                {activeTab === "growth" && <RequireAuth requiredRoles={['dev']} requiredPermissions={['cm_report_growth_report']} asComponent><GrowthContentV4 /></RequireAuth>}
                {activeTab === "room" && <RequireAuth requiredRoles={['dev']} requiredPermissions={['cm_report_room_report']} asComponent><RoomReportContent /></RequireAuth>}
            </div>
        </AdminLayout>
    );
};

export default ChannelManagerReport;